import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import SessionClientOnboard from 'dashboard/lib/clients/SessionClientOnboard';
import { Organization } from 'dashboard/models/organization';
import { User, UserAvailability, UserStatus } from 'dashboard/models/user';
import { saveCurrentUser } from 'dashboard/redux/actions/current-user';
import { setOrganization } from 'dashboard/redux/actions/organization';
import { setUser, setUsers } from 'dashboard/redux/actions/user';
import dashboardStore from 'dashboard/redux/store';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiEndpoint, ApiModel, ApiPayload, PaginationParams } from 'lib/interfaces/api';

export const loadPaginatedUserList =
  (
    pageNumber: number,
    pageSize: number,
    setPaginationMeta: (meta: PaginationParams) => void,
    filter: { omni?: string; statuses?: UserStatus[]; teamIds?: string[] } = {},
  ): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: users, meta } = await SessionClientDashboard.listResource<User, PaginationParams>(
      ApiEndpoint.User,
      {
        page: {
          number: pageNumber,
          size: pageSize,
        },
        filter: filter,
      },
    );

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setPaginationMeta(meta);
    dispatch(setUsers(users));
  };

export const loadPaginatedUserListWithPermissionsContext =
  (
    pageNumber: number,
    pageSize: number,
    setPaginationMeta: (meta: PaginationParams) => void,
    filter: { omni?: string; statuses?: UserStatus[]; teamIds?: string[] } = {},
  ): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: users, meta } = await SessionClientDashboard.listResource<User, PaginationParams>(
      ApiEndpoint.User,
      {
        page: {
          number: pageNumber,
          size: pageSize,
        },
        filter: filter,
        query: 'excess-permissions',
      },
    );

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setPaginationMeta(meta);
    dispatch(setUsers(users));
  };

// WARNING (@rick): ONLY USE THIS METHOD IF YOU KNOW WHAT YOU'RE DOING.
//
// We should generally not use this method, especially if you plan to filter for
// a subset of users after (e.g. event timelines). Instead, rely on the backend to provide the
// associated users via `includes`.
export const getAllUsers = async () => {
  let nextPage = 1;
  let totalPages = null;

  const users: User[] = [];
  while (totalPages == null || nextPage <= totalPages) {
    const response = await SessionClientDashboard.listResource<User, PaginationParams>(
      ApiEndpoint.User,
      {
        page: {
          number: nextPage,
          size: 200,
        },
        headers: {
          'Persona-Skip-Log-User-Action': 'true',
        },
      },
    );

    users.push(...response.data);

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    nextPage = response.meta['current-page'] + 1;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    totalPages = response.meta['total-pages'];
  }

  return users;
};

export const loadUserList = (): DashboardThunkAction<Promise<void>> => async (dispatch) => {
  const users = await getAllUsers();

  dispatch(setUsers(users));
};

/**
 * Onboard thunk actions
 */
export const loadInvitedUser =
  (userToken: string, activationToken: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: user, included } = await SessionClientOnboard.getResource<User>(
      ApiEndpoint.User,
      userToken,
      {
        activationToken,
      },
    );

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const organization = included.find(
      ({ id }) => id === user.relationships.organization.data.id,
    ) as Organization;
    dispatch(setOrganization(organization));
    dispatch(saveCurrentUser(user));
  };

interface UserAcceptInviteAttributes extends Pick<User['attributes'], 'name-first' | 'name-last'> {
  password: string;
  'password-confirmation': string;
}

export const acceptInvite =
  ({
    firstName,
    lastName,
    password,
    passwordConfirmation,
    userToken,
    activationToken,
  }: {
    firstName: string;
    lastName: string;
    password: string;
    passwordConfirmation: string;
    userToken: string;
    activationToken: string;
  }): DashboardThunkAction<Promise<void>> =>
  async () => {
    await SessionClientOnboard.postResourceAction<
      User,
      ApiPayload<UserAcceptInviteAttributes, ApiModel.User>
    >(
      {
        attributes: {
          'name-first': firstName,
          'name-last': lastName,
          password,
          'password-confirmation': passwordConfirmation,
        },
        type: ApiModel.User,
        id: userToken,
      },
      'activate',
      {
        meta: {
          'activation-token': activationToken,
        },
      },
    );
  };

/**
 * User Availability
 */

export const updateUserAvailability = async (userId: string, availability: UserAvailability) => {
  const response = await SessionClientDashboard.updateResource<User>({
    id: userId,
    type: ApiModel.User,
    attributes: {
      availability: availability,
    },
  });
  dashboardStore.dispatch(setUser(response.data));
};
